import { useState } from "react";

const WHATSAPP_NUMBER = "5511991882938";

/**
 * Hook customizado para enviar pedidos para WhatsApp
 * Encapsula toda a lógica de envio de mensagens WhatsApp
 */
export const useSendToWhatsApp = () => {
  const [loading, setLoading] = useState(false);

  /**
   * Envia o pedido para WhatsApp após criação bem-sucedida
   * @param {string} codigo - Código do pedido criado
   * @param {string} name - Nome do cliente
   * @param {string} phoneDigits - Telefone do cliente (apenas dígitos)
   * @param {string} deliveryType - Tipo de entrega (pickup ou delivery)
   * @param {object} address - Endereço para entrega
   * @param {array} cart - Itens do carrinho
   * @param {function} getTotalPrice - Função para calcular total
   * @param {string} paymentMethodSelected - Método de pagamento selecionado
   * @param {object} freightData - Dados do frete (opcional)
   * @returns {object} { message, whatsappUrl }
   */
  const sendToWhatsApp = (
    codigo,
    name,
    phoneDigits,
    deliveryType,
    address,
    cart,
    getTotalPrice,
    paymentMethodSelected,
    freightData = null
  ) => {
    try {
      setLoading(true);

      const message = buildMessage(
        codigo,
        name,
        phoneDigits,
        deliveryType,
        address,
        cart,
        getTotalPrice,
        paymentMethodSelected,
        freightData
      );

      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

      // Abre o WhatsApp Web em nova aba
      window.open(whatsappUrl, "_blank");

      return { message, whatsappUrl };
    } catch (error) {
      console.error("Erro ao enviar para WhatsApp:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Constrói a mensagem formatada para WhatsApp
   */
  const buildMessage = (
    codigo,
    name,
    phoneDigits,
    deliveryType,
    address,
    cart,
    getTotalPrice,
    paymentMethodSelected,
    freightData = null
  ) => {
    const formatBRL = (value) => {
      const n = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(n)) return "—";
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(n);
    };

    const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

    let message = `*PEDIDO BONFIM GOURMET*\n\n`;
    message += `*Pedido:* ${codigo}\n`;
    message += `*Cliente:* ${name}\n`;
    message += `*Telefone:* ${phoneDigits}\n`;
    message += `*Tipo:* ${
      deliveryType === "delivery" ? "Delivery" : "Retirada"
    }\n\n`;

    if (deliveryType === "delivery") {
      const cepDigits = onlyDigits(address.cep);
      const addressOrders = `${address.logradouro}, Nº ${address.numero}${
        address.complemento ? " (" + address.complemento + ")" : ""
      }`;

      message += `*ENDERECO DE ENTREGA:*\n`;
      message += `CEP: ${cepDigits}\n`;
      message += `Endereco: ${addressOrders}\n`;
      message += `Bairro: ${address.bairro}\n`;
      message += `UF/Estado: ${address.uf}${
        address.estado ? " - " + address.estado : ""
      }\n`;

      const mapsQuery = [
        address.logradouro,
        address.numero ? " " + address.numero : "",
        address.complemento ? " " + address.complemento : "",
        address.bairro ? ", " + address.bairro : "",
        address.uf ? " - " + address.uf : "",
        address.estado ? ", " + address.estado : "",
      ]
        .join("")
        .trim();
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        mapsQuery
      )}`;
      message += `Mapa: ${mapsUrl}\n`;

      // Adiciona informações de frete se disponível
      if (freightData?.available) {
        message += `\n*INFORMACOES DE FRETE:*\n`;
        message += `Distancia: ${freightData.distanceKm.toFixed(2)} km\n`;
        message += `Valor do frete: ${formatBRL(freightData.value)}\n`;
      }

      message += `\n`;
    }

    message += `\n*ITENS DO PEDIDO:*\n`;
    cart.forEach((item, index) => {
      message += `\n${index + 1}. *${item.product_name}*\n`;
      message += `Quantidade: ${item.quantity}\n`;
      message += `Valor unitario: ${formatBRL(item.product_price)}\n`;
      if (item.comments) {
        message += `Observacoes: ${item.comments}\n`;
      }
      message += `Subtotal: ${formatBRL(item.product_price * item.quantity)}\n`;
    });

    const subtotal = getTotalPrice();
    const freightValue = freightData?.available ? freightData.value : 0;
    const total = subtotal + freightValue;

    message += `\n*SUBTOTAL: ${formatBRL(subtotal)}*`;
    if (freightValue > 0) {
      message += `\n*FRETE: ${formatBRL(freightValue)}*`;
    }
    message += `\n*TOTAL: ${formatBRL(total)}*`;
    message += `\n\n*PAGAMENTO:* Vou pagar com ${paymentMethodSelected}\n`;

    return message;
  };

  return {
    loading,
    sendToWhatsApp,
    buildMessage,
  };
};
